import React, { useState } from "react";
import { Alert, Box, Button, Divider, TextField, Typography, styled } from "@mui/material";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useProcessOrderMutation } from "../../services/payments/payments";
import { useGetDeliveryZonesQuery } from "../../services/deliveryZones/deliveryZones";
import { clearCart } from "../../services/cartSlice/cartSlice";
import { RootState, AppDispatch } from "../../app/store";
import Cart from "../../components/Cart/Cart";
import CartTotals from "../../components/CartTotals/CartTotals";
import { PageLayout } from "../../components/PageLayout/PageLayout";
import {
  findActiveZone,
  useDeliverySelection,
  useReconcileMunicipality,
} from "../../hooks/useDeliverySelection";
import { resolveCartLeadTimeDays } from "../../utils/availability";
import {
  composePromise,
  DELIVERY_PICKUP,
  isQuoteError,
  quoteDelivery,
} from "../../utils/deliveryQuote";
import { calculateDiscountedPrice } from "../../utils/format";
import { generateOrderId, validatePhone } from "../shared/utils";
import { gaEvent, toGaItem } from "../../analytics";
import { getErrorMessage, getErrorStatus, HTTP_BAD_REQUEST } from "../../utils/apiError";
import { DEFAULT_LEAD_TIME_DAYS, DELIVERY_TEXTS, PROCESS_ORDER_TEXTS } from "../shared/constants";
import DeliverySection from "./DeliverySection";

const Sheet = styled(Box)(({ theme }) => ({
  backgroundColor: theme.palette.common.white,
  color: theme.palette.text.primary,
  padding: theme.spacing(2),
}));

const Section = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2),
  marginTop: theme.spacing(2),
  borderRadius: theme.spacing(1),
  border: `1px solid ${theme.palette.divider}`,
}));

const ConfirmButton = styled(Button)(({ theme }) => ({
  borderRadius: 999,
  textTransform: "none",
  fontWeight: 700,
  padding: theme.spacing(1.25, 3),
}));

/**
 * Checkout: one page, five labelled sections, in the order a shopper reasons about them
 * (docs/ordering-flow/README.md §7.2). Not a wizard — it collects four fields.
 *
 * What it no longer does is jump to WhatsApp. The old page assigned `window.location.href` the
 * instant the order posted, which meant the shopper never saw a total, never saw a delivery date,
 * and never saw the order id they were about to send to an advisor. It now navigates to
 * `/pedido/:id`, which already existed, already renders the order, and is already the link the
 * confirmation SMS points at. That inserts one tap before the sale closes; `whatsapp_click` carries
 * a `source` dimension, so the cost of that tap is measurable rather than argued about (§7.3).
 */
const ProcessOrderPage: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();

  const cartItems = useSelector((state: RootState) => state.cart.items);
  const itemsSubtotal = useSelector((state: RootState) => state.cart.itemsSubtotal);
  const quoteItemCount = useSelector((state: RootState) => state.cart.quoteItemCount);

  const { data: delivery } = useGetDeliveryZonesQuery();
  const { method, municipalityId, setMethod, setMunicipalityId } = useDeliverySelection();
  useReconcileMunicipality(delivery?.zones, municipalityId, setMunicipalityId);

  const [processOrder, { isLoading }] = useProcessOrderMutation();

  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [address, setAddress] = useState("");
  const [instructions, setInstructions] = useState("");

  const [nameError, setNameError] = useState<string | null>(null);
  const [phoneError, setPhoneError] = useState<string | null>(null);
  const [municipalityError, setMunicipalityError] = useState<string | null>(null);
  const [addressError, setAddressError] = useState<string | null>(null);
  const [serverError, setServerError] = useState<string | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const isPickup = method === DELIVERY_PICKUP;
  const zone = findActiveZone(delivery?.zones, municipalityId);
  const defaultLeadTimeDays = delivery?.config?.default_lead_time_days ?? DEFAULT_LEAD_TIME_DAYS;

  // A preview, deliberately. `POST /orders` recomputes all of this from the zone table and its
  // answer is what the order records — so a disagreement corrects the shopper's total on the
  // confirmation page rather than charging them what this browser guessed (§6.2).
  const quote = quoteDelivery({
    method,
    zone,
    itemsSubtotal,
    pickupEnabled: delivery?.config?.pickup_enabled !== false,
  });
  const deliveryFee = isQuoteError(quote) ? null : quote.fee;
  const promise = isQuoteError(quote)
    ? null
    : composePromise({
        cartLeadTimeDays: resolveCartLeadTimeDays(cartItems, defaultLeadTimeDays),
        etaDaysMin: quote.etaDaysMin,
        etaDaysMax: quote.etaDaysMax,
      });

  const validate = (): boolean => {
    let valid = true;

    if (!name.trim()) {
      setNameError(PROCESS_ORDER_TEXTS.nameRequired);
      valid = false;
    } else setNameError(null);

    if (!phone.trim()) {
      setPhoneError(PROCESS_ORDER_TEXTS.phoneRequired);
      valid = false;
    } else if (!validatePhone(phone)) {
      setPhoneError(PROCESS_ORDER_TEXTS.phoneInvalid);
      valid = false;
    } else setPhoneError(null);

    // Both only apply to a home delivery. Demanding an address for a pickup would be theatre, and
    // the server agrees — it checks the same pair, and its check is the one that counts.
    if (!isPickup && !municipalityId) {
      setMunicipalityError(DELIVERY_TEXTS.municipalityRequired);
      valid = false;
    } else setMunicipalityError(null);

    if (!isPickup && !address.trim()) {
      setAddressError(DELIVERY_TEXTS.addressRequired);
      valid = false;
    } else setAddressError(null);

    return valid;
  };

  const handleSubmit = async () => {
    setServerError(null);
    setSubmitError(null);
    if (!validate() || cartItems.length === 0) return;

    /**
     * `add_shipping_info` — the step between `begin_checkout` and `purchase` that GA4's funnel
     * report expects and this checkout never sent (EB-558).
     *
     * Fired here, on a submit that passed validation, rather than from the municipality selector.
     * The selector is touched on the product page, re-touched in checkout, and reconciled
     * automatically when a stored municipality goes stale — reporting each of those would inflate
     * the step above `purchase` and make the real drop-off unreadable. One submit, one event.
     *
     * `shipping` and `delivery_municipality` are the whole point: a fee first seen at the last
     * screen is the classic abandonment cause, and without the municipality on the event there is
     * no way to tell which zone's fee is the one losing orders.
     */
    gaEvent("add_shipping_info", {
      currency: "COP",
      value: itemsSubtotal,
      shipping_tier: isPickup ? "recogida" : "domicilio",
      shipping: deliveryFee ?? 0,
      ...(isPickup ? {} : { delivery_municipality: zone?.municipality || municipalityId }),
      items: cartItems.map((item) => toGaItem(item, { quantity: item.quantity })),
    });

    const order = {
      id: generateOrderId(),
      name,
      phone,
      status: "PENDING",
      email: "",
      items: cartItems.map((item) => ({
        item_id: item.id,
        name: item.name,
        price: item.price,
        quantity: item.quantity,
        picture: item.picture,
        discount: item.discount,
        discounted_price: calculateDiscountedPrice(item.price, item.discount),
        // Sent so the server can derive what this line was promised. It re-derives rather than
        // trusting a submitted `availability_state`, but it needs the facts to do it from.
        available_quantity: item.available_quantity,
        lead_time_days: item.lead_time_days,
      })),
      delivery_method: method,
      delivery_municipality_id: isPickup ? "" : municipalityId,
      address: isPickup ? "" : address,
      delivery_instructions: isPickup ? "" : instructions,
      delivery_status: "PENDING",
      customer_notes: "",
      order_time: new Date().toISOString(),
      user_id: "",
      user_has_been_contacted: false,
      payment_method: "",
      delivery_date: "",
      // Sent for the record, not for the arithmetic: the server compares it against its own figure
      // and flags a mismatch on the order rather than failing it.
      order_total: itemsSubtotal + (deliveryFee ?? 0),
    };

    try {
      const response = await processOrder(order).unwrap();

      gaEvent("purchase", {
        transaction_id: response.orderId,
        currency: "COP",
        // The server's number, not the one just posted — GA4 should record what was charged.
        value: response.order_total,
        shipping: response.delivery_fee,
        items: cartItems.map((item) => toGaItem(item, { quantity: item.quantity })),
      });

      dispatch(clearCart());
      navigate(`/pedido/${response.orderId}`);
    } catch (error) {
      // A 400 is the server refusing this delivery and saying why — it belongs in the Entrega
      // section, next to the field that caused it, not in a generic banner at the bottom.
      const message = getErrorMessage(error);
      const status = getErrorStatus(error);
      const isRejected = status === HTTP_BAD_REQUEST && Boolean(message);

      if (isRejected) setServerError(message);
      else setSubmitError(PROCESS_ORDER_TEXTS.orderError);

      /**
       * `order_error` (EB-558). A checkout that fails at the last step is currently invisible: the
       * funnel shows `begin_checkout` with no `purchase` and nothing distinguishes "changed their
       * mind" from "we refused the order".
       *
       * The two kinds are kept apart because they need opposite responses. `delivery_rejected` is
       * the server declining a zone — the shopper saw a reason and can fix it, so a spike means a
       * zone table that disagrees with what the storefront is offering. `request_failed` is
       * everything else: a 5xx, a timeout, a dropped connection. Collapsing them would bury an
       * outage inside what looks like ordinary validation noise.
       *
       * `error_message` is only carried for the rejection, where it is the server's own delivery
       * copy. A raw failure's message is a stack-shaped string with no reporting value.
       */
      gaEvent("order_error", {
        error_type: isRejected ? "delivery_rejected" : "request_failed",
        error_status: status ?? 0,
        delivery_method: method,
        value: itemsSubtotal + (deliveryFee ?? 0),
        ...(isRejected ? { error_message: message } : {}),
      });
    }
  };

  const isEmpty = cartItems.length === 0;

  return (
    <PageLayout>
      <Sheet>
        <Typography variant="h5" fontWeight="bold" gutterBottom>
          {PROCESS_ORDER_TEXTS.title}
        </Typography>

        <Section>
          <Typography variant="h6" fontWeight="bold" gutterBottom>
            {PROCESS_ORDER_TEXTS.summaryTitle}
          </Typography>
          <Cart />
        </Section>

        <Section>
          <DeliverySection
            zones={delivery?.zones}
            config={delivery?.config}
            method={method}
            municipalityId={municipalityId}
            address={address}
            instructions={instructions}
            municipalityError={municipalityError}
            addressError={addressError}
            serverError={serverError}
            onMethodChange={setMethod}
            onMunicipalityChange={setMunicipalityId}
            onAddressChange={setAddress}
            onInstructionsChange={setInstructions}
          />
        </Section>

        <Section>
          <Typography variant="h6" fontWeight="bold" gutterBottom>
            {PROCESS_ORDER_TEXTS.contactTitle}
          </Typography>
          <Typography variant="body2" color="text.secondary" mb={2}>
            {PROCESS_ORDER_TEXTS.contactDescription}
          </Typography>
          <Box display="flex" flexDirection="column" gap={2}>
            <TextField
              label={PROCESS_ORDER_TEXTS.nameLabel}
              value={name}
              onChange={(event) => setName(event.target.value)}
              error={Boolean(nameError)}
              helperText={nameError ?? ""}
              size="small"
              fullWidth
            />
            <TextField
              label={PROCESS_ORDER_TEXTS.phoneLabel}
              value={phone}
              onChange={(event) => setPhone(event.target.value)}
              error={Boolean(phoneError)}
              helperText={phoneError ?? ""}
              size="small"
              fullWidth
            />
          </Box>
        </Section>

        <Section>
          <Typography variant="h6" fontWeight="bold" gutterBottom>
            {PROCESS_ORDER_TEXTS.totalTitle}
          </Typography>
          <CartTotals
            itemsSubtotal={itemsSubtotal}
            quoteItemCount={quoteItemCount}
            deliveryFee={deliveryFee}
          />
          {promise && (
            <Typography variant="body2" color="text.secondary" mt={1}>
              {DELIVERY_TEXTS.promise(promise.daysMin, promise.daysMax)}
              {quoteItemCount > 0 && ` ${PROCESS_ORDER_TEXTS.promiseCaveat}`}
            </Typography>
          )}
          <Divider sx={{ my: 2 }} />

          {submitError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {submitError}
            </Alert>
          )}
          {isEmpty && <Alert severity="info">{PROCESS_ORDER_TEXTS.emptyCart}</Alert>}

          <Box display="flex" justifyContent="flex-end" mt={1}>
            <ConfirmButton
              variant="contained"
              color="success"
              onClick={handleSubmit}
              disabled={isLoading || isEmpty}
              endIcon={<WhatsAppIcon />}
            >
              {isLoading ? PROCESS_ORDER_TEXTS.submitLoading : PROCESS_ORDER_TEXTS.submitButton}
            </ConfirmButton>
          </Box>
        </Section>
      </Sheet>
    </PageLayout>
  );
};

export default ProcessOrderPage;
